<div class="flex flex-column gap-3 mb-4">
    <div>
        <h2 class="m-0 text-xl font-bold">Pipeline overview</h2>
        <p class="m-0 mt-1 text-sm text-color-secondary">{{ filterHint() }}</p>
    </div>

    @if (loading) {
        <div class="card p-5 text-center text-color-secondary border-1 border-dashed surface-border border-round">
            Loading charts…
        </div>
    } @else if (!summary) {
        <div class="card p-5 text-center text-color-secondary border-1 border-dashed surface-border border-round">
            No summary data available.
        </div>
    } @else {
        <div class="grid">
            <div class="col-12 md:col-6 xl:col-3">
                <div class="card p-3 flex flex-column gap-1 h-full">
                    <span class="text-xs font-bold uppercase text-color-secondary">Total districts</span>
                    <span class="text-2xl font-bold">{{ summary.overview.total_districts | number }}</span>
                </div>
            </div>
            <div class="col-12 md:col-6 xl:col-3">
                <div class="card p-3 flex flex-column gap-1 h-full"
                     pTooltip="Districts with a non-empty agenda_url in the catalog"
                     tooltipPosition="top"
                     appendTo="body">
                    <span class="text-xs font-bold uppercase text-color-secondary">With agenda URL</span>
                    <span class="text-2xl font-bold text-primary">
                        {{ summary.overview.districts_with_agenda_url | number }}
                        <span class="text-base font-semibold text-color-secondary">
                            ({{ summary.overview.agenda_url_pct }}%)
                        </span>
                    </span>
                </div>
            </div>
            <div class="col-12 md:col-6 xl:col-3">
                <div class="card p-3 flex flex-column gap-1 h-full"
                     pTooltip="Distinct districts with at least one crawl run"
                     tooltipPosition="top"
                     appendTo="body">
                    <span class="text-xs font-bold uppercase text-color-secondary">Crawled</span>
                    <span class="text-2xl font-bold text-green-600">
                        {{ summary.overview.districts_crawled | number }}
                        <span class="text-base font-semibold text-color-secondary">
                            ({{ summary.overview.crawled_pct }}%)
                        </span>
                    </span>
                    <span class="text-xs text-color-secondary">
                        {{ summary.overview.crawled_of_agenda_pct }}% of districts with agenda URL
                    </span>
                </div>
            </div>
            <div class="col-12 md:col-6 xl:col-3">
                <div class="card p-3 flex flex-column gap-1 h-full"
                     pTooltip="Distinct districts with at least one completed analyze run"
                     tooltipPosition="top"
                     appendTo="body">
                    <span class="text-xs font-bold uppercase text-color-secondary">Analyzed</span>
                    <span class="text-2xl font-bold text-purple-600">
                        {{ summary.overview.districts_analyzed | number }}
                        <span class="text-base font-semibold text-color-secondary">
                            ({{ summary.overview.analyzed_pct }}%)
                        </span>
                    </span>
                    <span class="text-xs text-color-secondary">
                        {{ summary.overview.analyzed_of_agenda_pct }}% of districts with agenda URL
                    </span>
                </div>
            </div>
        </div>

        <div class="card p-3 flex flex-column">
            <h3 class="m-0 mb-1 text-base font-bold">Pipeline progress</h3>
            <p class="m-0 mb-3 text-sm text-color-secondary">
                Select state to view pipeline progress for each year
            </p>

            <div class="flex flex-wrap gap-3 mb-4">
                <div class="flex flex-column gap-1 w-12rem">
                    <label class="text-xs font-bold uppercase text-color-secondary" for="pipeline-state">
                        State
                    </label>
                    <p-select inputId="pipeline-state"
                              [options]="stateOptions"
                              [(ngModel)]="selectedState"
                              (ngModelChange)="onStateChange()"
                              optionLabel="label"
                              optionValue="value"
                              placeholder="Select state"
                              appendTo="body"
                              class="w-full" />
                </div>
            </div>

            @if (detailLoading) {
                <div class="p-5 text-center text-color-secondary border-1 border-dashed surface-border border-round">
                    Loading charts…
                </div>
            } @else if (!selectedState) {
                <div class="p-5 text-center text-color-secondary border-1 border-dashed surface-border border-round">
                    Select state to view pipeline progress.
                </div>
            } @else if (!yearCharts.length) {
                <div class="p-5 text-center text-color-secondary border-1 border-dashed surface-border border-round">
                    No data for {{ selectedState }}.
                </div>
            } @else {
                <div [ngClass]="yearChartsRowClass()">
                    @for (block of yearCharts; track block.year) {
                        <div [ngClass]="yearCardWrapperClass()">
                            <div [ngClass]="yearCardClass()">
                                <span class="text-sm font-semibold">{{ block.year }}</span>
                                <span class="text-xs text-color-secondary text-center">
                                    {{ block.row.total_districts | number }} total districts
                                    ·
                                    {{ block.row.districts_with_agenda_url | number }} with agenda URL
                                </span>

                                @if (!block.hasPipelineCounts) {
                                    <div class="p-4 text-center text-sm text-color-secondary border-1 border-dashed surface-border border-round w-full">
                                        No pipeline activity
                                    </div>
                                } @else if (!block.chartData) {
                                    <div class="p-4 text-center text-sm text-color-secondary border-1 border-dashed surface-border border-round w-full">
                                        No districts to chart
                                    </div>
                                } @else {
                                    <div class="w-full max-w-12rem">
                                        <p-chart type="doughnut"
                                                 [data]="block.chartData"
                                                 [options]="block.chartOptions"
                                                 height="180" />
                                    </div>
                                    <div class="flex flex-column gap-1 text-xs w-full">
                                        @for (segment of block.segments; track segment.key) {
                                            <span class="flex align-items-center gap-2">
                                                <span class="border-circle inline-block flex-shrink-0"
                                                      style="width: 0.55rem; height: 0.55rem"
                                                      [style.backgroundColor]="segment.color">
                                                </span>
                                                <span class="white-space-nowrap overflow-hidden text-overflow-ellipsis">
                                                    {{ segment.label }}: {{ segment.value | number }}
                                                </span>
                                            </span>
                                        }
                                    </div>
                                    <span class="text-xs text-color-secondary text-center">
                                        of {{ block.row.districts_with_agenda_url | number }} with agenda URL
                                    </span>
                                }
                            </div>
                        </div>
                    }
                </div>
            }
        </div>
    }
</div>
